<template>
	<div class="zoom-bar">
		<div
			ref="trackViewport"
			class="zoom-bar__track-viewport"
		>
			<div
				ref="track"
				class="zoom-bar__track"
				:style="trackStyle"
				@mousedown.stop="onSliderMouseDown"
				@touchstart.stop="onSliderMouseDown"
			>
				<div class="zoom-bar__tags js-zoom-tags-track">
					<ZoomBarTag
						v-for="tag in flatTags"
						:key="`slider-${tag.id}`"
						:duration="duration"
						:is-edited="editedTag && tag.id === editedTag.id"
						:time="editedTag && tag.id === editedTag.id ? editedTag.time : tag.time"
						:type="tag.type"
					/>
				</div>
				<div
					ref="slider"
					class="zoom-bar__slider"
					:class="{ 'is-active': !isIdle }"
					:style="sliderStyle"
					@mousedown.stop="onSliderMouseDown"
					@touchstart.stop="onSliderMouseDown"
				>
					<div
						ref="handlerLeft"
						class="zoom-bar__handler -left"
					/>
					<div
						ref="handlerRight"
						class="zoom-bar__handler -right"
					/>
				</div>
			</div>
			<div
				v-if="trackCanBeScrolledLeft"
				ref="shadowLeft"
				class="zoom-bar__scroll-shadow -left"
				@mousedown.stop="onSliderMouseDown"
			/>
			<div
				v-if="trackCanBeScrolledRight"
				ref="shadowRight"
				class="zoom-bar__scroll-shadow -right"
				@mousedown.stop="onSliderMouseDown"
			/>
		</div>
	</div>
</template>

<script>
import ZoomBarTag from './ZoomBarTag';

const MODE = {
	IDLE:                'IDLE',
	SLIDER_MOVE:         'SLIDER_MOVE',
	SLIDER_RESIZE_LEFT:  'SLIDER_RESIZE_LEFT',
	SLIDER_RESIZE_RIGHT: 'SLIDER_RESIZE_RIGHT',
	TRACK_MOVE:          'TRACK_MOVE',
	AUTOSCROLL:          'AUTOSCROLL',
};

const MIN_SLIDER_WIDTH = 32;
const DIR_LEFT = 'left';
const DIR_RIGHT = 'right';
const AUTOSCROLL_STEP = 15; // the autoscroll's step (speed) in pixels when dragging the slider close to the container edges
const AUTOSCROLL_TRIGGER_OFFSET = 20;
const SLIDER_SIZE_CHANGED = 'SIZE_CHANGED';
const SLIDER_OFFSET_CHANGED = 'SLIDER_OFFSET_CHANGED';

let raf = false;

const getSliderConstraints = (width, leftEdgeConstraint, rightEdgeConstraint) => {
	return {
		min: leftEdgeConstraint,
		max: rightEdgeConstraint - width,
	};
};

export default {
	name:       'ZoomBar',
	components: {
		ZoomBarTag,
	},
	props: {
		// the start offset in seconds at which the slider gets positioned
		startOffset: {
			type:    Number,
			default: 0,
		},
		zoomLevel: {
			type:    Number,
			default: 1,
		},
		// total duration of the video
		duration: {
			type:    Number,
			default: 0,
		},
		// tags
		tags: {
			type:    Array,
			default: () => [],
		},
		editedTag: {
			type:    Object,
			default: () => null,
		},
	},
	data() {
		return {
			sliderOffset:            0, // slider's left offset relative to parent container (the track) in px
			sliderWidth:             MIN_SLIDER_WIDTH,
			initialClientX:          null, // mouse x initial position (when mouse interaction started)
			initialSliderLeftOffset: null,
			initialSliderWidth:      null,
			trackViewportWidth:      0,
			trackWidth:              0,
			trackOffset:             0,
			initialTrackOffset:      0,
			mode:                    MODE.IDLE,
			autoscrollDir:           null,
			autoscrollInterval:      null,
			resizeObserver:          null,
			isAltKeyPressed:         false, // Track alt key state
		};
	},
	computed: {
		flatTags() {
			return this.tags.reduce((acc, curr) => {
				if (curr.isGroup) acc.push(...curr.tags.map((t) => t.tag));
				else acc.push(curr);
				return acc;
			}, []);
		},
		sliderStyle() {
			return {
				left:      `${this.sliderOffset}px`,
				flexBasis: `${this.sliderWidth}px`,
				maxWidth:  `${this.sliderWidth}px`,
			};
		},
		trackStyle() {
			const percent = (this.trackWidth / this.trackViewportWidth) * 100;

			return {
				width:     `calc(${percent}%)`,
				transform: `translateX(-${this.trackOffset}px)`,
			};
		},
		isIdle() {
			return this.mode === MODE.IDLE;
		},
		offsetStartToPixels() {
			return this.duration ? (this.startOffset / this.duration) * this.trackWidth : 0;
		},
		sliderLeftOffsetToSeconds() {
			return (this.duration * this.sliderOffset) / this.trackWidth;
		},
		zoomLevelToSliderWidth() {
			return this.trackWidth / this.zoomLevel;
		},
		sliderWidthToZoomLevel() {
			return this.trackWidth / this.sliderWidth;
		},
		trackCanBeScrolledLeft() {
			return this.trackOffset > 0;
		},
		trackCanBeScrolledRight() {
			return this.trackOffset + this.trackViewportWidth < this.trackWidth;
		},
	},
	watch: {
		zoomLevel() {
			this.initValues();
		},
		startOffset() {
			this.initValues();
		},
		isIdle() {
			this.$emit('is-changing', !this.isIdle);
		},
	},
	mounted() {
		this.trackViewportWidth = this.$refs.trackViewport.getBoundingClientRect().width;
		this.initValues();

		// Add keyboard event listeners
		window.addEventListener('keydown', this.handleKeyDown);
		window.addEventListener('keyup', this.handleKeyUp);

		if (window.ResizeObserver) {
			this.resizeObserver = new ResizeObserver(() => {
				if (!this.$refs.trackViewport) return;

				const newWidth = this.$refs.trackViewport.getBoundingClientRect().width;
				if (newWidth !== this.trackViewportWidth) {
					this.trackViewportWidth = newWidth;
					// Force recalculation of track width before initializing values
					this.trackWidth = 0;
					this.$nextTick(() => {
						this.initValues();
						// Ensure slider width is updated based on new track width
						if (this.mode === MODE.IDLE) {
							this.sliderWidth = this.zoomLevelToSliderWidth;
							this.adjustTrackWidthAndOffset(true);
						}
					});
				}
			});
			this.resizeObserver.observe(this.$refs.trackViewport);
		}
	},
	beforeDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		// Remove keyboard event listeners
		window.removeEventListener('keydown', this.handleKeyDown);
		window.removeEventListener('keyup', this.handleKeyUp);
	},
	methods: {
		initValues() {
			// first, adjust track width and offset according to the zoom level
			if (this.mode === MODE.IDLE) {
				this.adjustTrackWidthAndOffset();
				this.sliderOffset = this.offsetStartToPixels;
				this.sliderWidth = this.zoomLevelToSliderWidth;
			}
		},
		adjustTrackWidth() {
			// skip unnecessary auto adjustments
			if (this.zoomLevel === this.sliderWidthToZoomLevel && this.trackWidth > 0) {
				return;
			}
			const maxZoomLevel = this.trackViewportWidth / MIN_SLIDER_WIDTH;

			if (this.zoomLevel > maxZoomLevel) {
				const zoomFactor = this.zoomLevel / maxZoomLevel;
				this.trackWidth = this.trackViewportWidth * zoomFactor;
				return;
			}
			this.trackWidth = this.trackViewportWidth;
		},
		/**
		 * Adjusts the track offset so that the track's viewport will have the given center.
		 * We will usually want to center around the slider position.
		 *
		 * @param center
		 */
		centerTrack(center) {
			if (this.trackOffset + this.trackViewportWidth < center || this.trackOffset > center) {
				// calculate the maximum offset of the track
				const maxOffset = this.trackWidth - this.trackViewportWidth;
				// compute the track offset to have the slider centered inside it
				this.trackOffset = Math.max(0, Math.min(center - (this.trackViewportWidth - this.sliderWidth) / 2, maxOffset));
			}
		},
		adjustTrackWidthAndOffset(lockWidth) {
			if (!lockWidth) {
				this.adjustTrackWidth();
			}
			this.centerTrack(this.sliderOffset);
		},
		onSliderMouseDown(event) {
			const target = event.target;

			const trackClick = target.classList.contains('js-zoom-tags-track') || target.classList.contains('js-zoom-bar-tag');
			let isTargetSliderMove = false;

			// we still need to be able to drag the slider even when it gets 'under' the left/right shadows
			if (target === this.$refs.slider) {
				isTargetSliderMove = true;
			} else if ([this.$refs.shadowLeft, this.$refs.shadowRight].includes(target)) {
				// enable dragging behavior only when the shadow and the slider overlap
				const { x: sliderX } = this.$refs.slider.getBoundingClientRect();
				if (this.trackCanBeScrolledLeft) {
					const { x: shadowX, width: shadowWidth } = this.$refs.shadowLeft.getBoundingClientRect();
					if (shadowX + shadowWidth > sliderX) {
						isTargetSliderMove = true;
					}
				}
				if (this.trackCanBeScrolledRight && !isTargetSliderMove) {
					const { x: shadowX } = this.$refs.shadowRight.getBoundingClientRect();
					if (shadowX < sliderX) {
						isTargetSliderMove = true;
					}
				}
			}

			if (trackClick) {
				const clientX = this.getClientX(event);
				this.handleTrackClick(clientX);
				// change mode to slider move after clicking on the track
				this.mode = MODE.SLIDER_MOVE;
			} else if (target === this.$refs.handlerLeft) {
				this.mode = MODE.SLIDER_RESIZE_LEFT;
			} else if (target === this.$refs.handlerRight) {
				this.mode = MODE.SLIDER_RESIZE_RIGHT;
			} else if (isTargetSliderMove) {
				this.mode = MODE.SLIDER_MOVE;
			} else {
				return;
			}

			// register document listeners
			document.addEventListener('mouseup', this.handleMouseUp);
			document.addEventListener('mousemove', this.handleMouseMove);
			document.addEventListener('touchend', this.handleMouseUp);
			document.addEventListener('touchmove', this.handleMouseMove);

			// store initial values
			this.initialClientX = this.getClientX(event);
			this.initialSliderLeftOffset = this.sliderOffset;
			this.initialSliderWidth = this.sliderWidth;
			this.initialTrackOffset = this.trackOffset;
		},
		handleMouseMove(event) {
			const currentClientX = this.getClientX(event);

			switch (this.mode) {
			case MODE.SLIDER_RESIZE_LEFT: {
				this.handleSliderResize(currentClientX, this.initialClientX, MODE.SLIDER_RESIZE_LEFT);
				break;
			}
			case MODE.SLIDER_RESIZE_RIGHT: {
				this.handleSliderResize(currentClientX, this.initialClientX, MODE.SLIDER_RESIZE_RIGHT);
				break;
			}
			case MODE.TRACK_MOVE: {
				this.handleTrackMove(currentClientX);
				break;
			}
			case MODE.SLIDER_MOVE: {
				this.handleSliderMove(currentClientX, this.initialClientX);
				break;
			}
			case MODE.AUTOSCROLL: {
				if (this.shouldCancelAutoScrolling(event)) {
					this.mode = MODE.SLIDER_MOVE;
				}
				break;
			}
			default: {
				throw new Error(`Invalid mode: ${this.mode}`);
			}
			}
		},
		handleMouseUp() {
			this.mode = MODE.IDLE;
			this.initialSliderLeftOffset = 0;
			this.initialSliderWidth = 0;
			this.initialTrackOffset = 0;
			this.stopAutoScrolling();
			document.removeEventListener('mousemove', this.handleMouseMove);
			document.removeEventListener('mouseup', this.handleMouseUp);
			document.removeEventListener('touchmove', this.handleMouseMove);
			document.removeEventListener('touchend', this.handleMouseUp);
		},
		handleSliderResize(currentX, startX, mode = MODE.SLIDER_RESIZE_LEFT) {
			const diff = currentX - startX;
			const dir = mode === MODE.SLIDER_RESIZE_LEFT ? -1 : 1;
			let sliderWidth = Math.min(this.trackWidth, Math.max(MIN_SLIDER_WIDTH, this.initialSliderWidth + dir * diff));
			let leftOffsetChanged = false;

			if (this.isAltKeyPressed) {
				// When alt is pressed, resize equally from both sides
				const totalDiff = dir * diff;
				// Calculate new width based on the drag direction
				sliderWidth = Math.min(
					this.trackWidth,
					Math.max(MIN_SLIDER_WIDTH, this.initialSliderWidth + (totalDiff * 2))
				);

				// Calculate the center point of the slider
				const sliderCenter = this.initialSliderLeftOffset + (this.initialSliderWidth / 2);

				// Adjust the width and position to maintain the center point
				const newOffset = sliderCenter - (sliderWidth / 2);

				// Apply constraints
				const { min, max } = getSliderConstraints(sliderWidth, this.trackOffset, this.trackWidth);
				this.sliderOffset = Math.max(min, Math.min(max, newOffset));
				leftOffsetChanged = true;
			} else {
				// Original single-direction resize logic
				if (mode === MODE.SLIDER_RESIZE_LEFT) {
					const newOffset = this.initialSliderLeftOffset + diff;
					const { min, max } = getSliderConstraints(sliderWidth, this.trackOffset, this.initialSliderLeftOffset + this.initialSliderWidth);
					const constrainedOffset = Math.max(min, Math.min(max, newOffset));

					this.sliderOffset = constrainedOffset;
					leftOffsetChanged = true;
				} else {
					sliderWidth = Math.max(MIN_SLIDER_WIDTH, Math.min(this.trackWidth - this.initialSliderLeftOffset, sliderWidth));
				}
			}

			this.sliderWidth = sliderWidth;
			this.emitChanges([SLIDER_SIZE_CHANGED, ...(leftOffsetChanged ? [SLIDER_OFFSET_CHANGED] : [])]);
		},
		handleSliderMove(currentX, startX) {
			const diff = currentX - startX;
			// slider size should remain constant
			const sliderWidth = this.initialSliderWidth;
			let newOffset = this.initialSliderLeftOffset + diff;

			const { min, max } = getSliderConstraints(sliderWidth, 0, this.trackWidth);
			newOffset = Math.max(min, Math.min(max, newOffset));

			const dir = currentX < startX ? DIR_LEFT : DIR_RIGHT;

			// auto scroll the track when the slider gets close to start/end of the track viewport
			if (dir === DIR_LEFT && this.sliderOffset < this.trackOffset + AUTOSCROLL_TRIGGER_OFFSET && this.trackCanBeScrolledLeft) {
				// autoscroll left
				this.startAutoScrolling(dir);
			} else if (dir === DIR_RIGHT && this.sliderOffset + this.sliderWidth > this.trackOffset + this.trackViewportWidth - AUTOSCROLL_TRIGGER_OFFSET) {
				// autoscroll right
				this.startAutoScrolling(dir);
			} else {
				// no autoscroll, just slider movement to a new position
				this.stopAutoScrolling();

				// ensure that the slider is still within the visible area
				this.sliderOffset = Math.min(this.trackOffset + this.trackViewportWidth - this.sliderWidth, Math.max(newOffset, this.trackOffset));
				this.emitChanges([SLIDER_OFFSET_CHANGED]);
			}
		},
		handleTrackMove(currentX) {
			const diff = currentX - this.initialClientX;
			this.trackOffset = Math.max(0, Math.min(this.trackWidth - this.trackViewportWidth, this.initialTrackOffset - diff));
		},
		handleTrackClick(clientX) {
			// Get the track's bounding rectangle to calculate click position
			const trackRect = this.$refs.track.getBoundingClientRect();
			const clickPositionRelative = clientX - trackRect.left + this.trackOffset;

			// Calculate new slider position, centering it on the click
			let newOffset = clickPositionRelative - (this.sliderWidth / 2);

			// Ensure slider stays within track bounds
			const { min, max } = getSliderConstraints(this.sliderWidth, 0, this.trackWidth);
			newOffset = Math.max(min, Math.min(max, newOffset));

			// Update slider position
			this.sliderOffset = newOffset;

			// Emit changes since we moved the slider
			this.emitChanges([SLIDER_OFFSET_CHANGED]);
		},
		moveTrackBy(offset) {
			this.trackOffset = Math.max(0, Math.min(this.trackWidth - this.trackViewportWidth, this.trackOffset + offset));
		},
		moveSliderBy(offset) {
			this.sliderOffset = Math.max(0, Math.min(this.trackWidth - this.sliderWidth, this.sliderOffset + offset));
			this.emitChanges([SLIDER_OFFSET_CHANGED]);
		},
		getClientX(event) {
			return event.touches ? event.touches[0].clientX : event.clientX;
		},
		startAutoScrolling(dir) {
			this.mode = MODE.AUTOSCROLL;
			this.autoscrollDir = dir;
			this.stopAutoScrolling();
			const sign = dir === DIR_LEFT ? -1 : 1;

			this.autoscrollInterval = setInterval(() => {
				this.moveTrackBy(sign * AUTOSCROLL_STEP);
				this.moveSliderBy(sign * AUTOSCROLL_STEP);
			}, 15);
		},
		stopAutoScrolling() {
			if (this.autoscrollInterval) {
				clearInterval(this.autoscrollInterval);
			}
		},
		shouldCancelAutoScrolling(event) {
			const { width: sliderWidth, x: sliderClientX } = this.$refs.slider.getBoundingClientRect();
			const eventX = this.getClientX(event);
			const sliderCenter = sliderClientX + sliderWidth / 2;
			let shouldCancel = false;

			// if while mousedown, the mouse cursor is moved in the opposite direction
			// to the right or left of the middle of the slider, then cancel the scrolling
			// and potentially return to the SLIDER_MOVE mode
			if (
				(this.autoscrollDir === DIR_LEFT && eventX > sliderCenter) ||
				(this.autoscrollDir === DIR_RIGHT && eventX < sliderClientX + sliderWidth / 2)
			) {
				shouldCancel = true;
			}

			if (shouldCancel) {
				this.stopAutoScrolling();
				this.initialClientX = eventX;
				this.initialSliderLeftOffset = this.sliderOffset;
				this.initialTrackOffset = this.trackOffset;
			}

			return shouldCancel;
		},
		emitChanges(changes = []) {
			if (raf) {
				window.cancelAnimationFrame(raf);
			}

			raf = window.requestAnimationFrame(() => {
				if (changes.includes(SLIDER_SIZE_CHANGED)) {
					this.$emit('change-level', this.sliderWidthToZoomLevel);
				}
				if (changes.includes(SLIDER_OFFSET_CHANGED)) {
					this.$emit('change-start', this.sliderLeftOffsetToSeconds);
				}
			});
		},
		handleKeyDown(event) {
			if (event.key === 'Alt') {
				this.isAltKeyPressed = true;
			}
		},
		handleKeyUp(event) {
			if (event.key === 'Alt') {
				this.isAltKeyPressed = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import './timeline.scss';

.zoom-bar {
	position: relative;
	padding: 4px 0;
	margin-top: 16px;
}

.zoom-bar__track-viewport {
	width: 100%;
	clip-path: border-box;
}

.zoom-bar__track {
	position: relative;
	background-color: $timeline-slider-background;
	border-radius: 4px;
	height: 100%;
}

.zoom-bar__tags {
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
}

.zoom-bar__slider {
	position: relative;
	border: 2px solid $timeline-slider-border;
	border-radius: 4px;
	padding: 14px 0;
	cursor: pointer;
	min-width: 32px;
	flex-shrink: 1;
	box-sizing: border-box;
}

.zoom-bar__handler {
	width: 6px;
	border-radius: 4px;
	background: $timeline-slider-border;
	height: 80%;
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	cursor: col-resize;

	&::before {
		content: '';
		position: absolute;
		width: 20px;
		height: 100%;
		left: 50%;
		transform: translateX(-50%);
	}

	&.-left {
		left: -4px;
	}

	&.-right {
		right: -4px;
	}
}

.zoom-bar__scroll-shadow {
	position: absolute;
	top: 0;
	height: 100%;
	width: 30px;

	&.-left {
		left: 0;
		background: linear-gradient(90deg, var(--color-grey-900) 0%, rgba(var(--color-rgb-grey-850), 0) 100%);
	}

	&.-right {
		right: 0;
		background: linear-gradient(270deg, var(--color-grey-900) 0%, rgba(var(--color-rgb-grey-850), 0) 100%);
	}
}
</style>
